import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Compass, Loader2, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground flex flex-col" data-testid="admin-login-page">
      <div className="p-6 md:p-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Torna al sito
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md border border-border rounded-[2rem] p-8 md:p-12"
        >
          <div className="flex items-center gap-2 font-display font-extrabold tracking-tight text-lg">
            <Compass className="w-5 h-5 text-primary" strokeWidth={2.5} /> Area Riservata
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-6">Accedi alla dashboard</h1>
          <p className="text-muted-foreground mt-2">Gestisci le iscrizioni al corso.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="flex flex-col gap-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-lg focus-visible:ring-2 focus-visible:ring-primary" placeholder="admin@osmandcorso.it" />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-lg focus-visible:ring-2 focus-visible:ring-primary" placeholder="••••••••" />
            </div>
            {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 uppercase tracking-[0.2em] text-xs font-semibold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
